import { useState } from "react";
import { BadgeDollarSign, Check, Loader2, Undo2 } from "lucide-react";
import api, { errMsg, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const TIMINGS = [
  { key: "now", label: "Pay at completion", hint: "marked paid the moment the trip closes" },
  { key: "later", label: "Pay later", hint: "waits for your next payout run" },
];

/** What the chauffeur is told they earn, and when they get it. The passenger's fare never appears. */
export const DriverPayCard = ({ trip, onSaved }) => {
  const payout = trip.driver_payout || {};
  const paid = trip.payout_paid || {};
  const [amount, setAmount] = useState(payout.amount ?? "");
  const [label, setLabel] = useState(payout.label || "");
  const [timing, setTiming] = useState(trip.payout_timing || "");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const run = async (key, call) => {
    setBusy(key); setError(""); setSaved(false);
    try {
      await call();
      setSaved(true);
      if (onSaved) await onSaved();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const save = () => run("save", () => api.put(`/manifests/${trip.id}/driver-pay`, {
    amount: Number(amount || 0), label: label.trim(), timing: timing || null,
  }));
  const payNow = () => run("pay", () => api.post(`/manifests/${trip.id}/payout/pay`, {}));
  const undoPay = () => run("undo", () => api.delete(`/manifests/${trip.id}/payout/pay`));

  return (
    <div data-testid="driver-pay-card" className="no-print rounded-xl border border-ink-600 bg-ink-800/60 p-5">
      <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Chauffeur pay for this trip</p>
      <p className="mt-2 text-sm text-slate-400">
        This is the only money figure the chauffeur sees — in the app, on the emailed manifest and on
        their payout statement. Your customer's fare is never shown to them.
      </p>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-[160px_1fr_auto]">
        <Input data-testid="driver-pay-amount" type="number" step="0.01" min="0" placeholder="0.00"
          value={amount} onChange={(e) => setAmount(e.target.value)}
          className="h-11 border-ink-600 bg-ink-900 text-slate-100 focus-visible:ring-gold" />
        <Input data-testid="driver-pay-label" placeholder="Agreed for this trip"
          value={label} onChange={(e) => setLabel(e.target.value)}
          className="h-11 border-ink-600 bg-ink-900 text-slate-100 placeholder:text-slate-600 focus-visible:ring-gold" />
        <Button data-testid="driver-pay-save" disabled={busy !== ""} onClick={save}
          className="h-11 bg-gold px-6 text-ink-900 hover:bg-gold-light">
          {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><BadgeDollarSign className="mr-2 h-4 w-4" /> Save pay</>)}
        </Button>
      </div>

      {paid.at ? (
        <div data-testid="driver-pay-paid" className="mt-4 flex flex-col gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-emerald-300">
            <Check className="mr-1.5 inline h-4 w-4" />
            Paid {money(paid.amount)} by {paid.method_label?.toLowerCase() || "hand"}
            {paid.by ? ` · ${paid.by}` : ""} — kept off the next payout run.
          </p>
          <Button data-testid="driver-pay-undo" variant="ghost" size="sm" disabled={busy !== ""}
            onClick={undoPay} className="shrink-0 text-slate-400 hover:text-gold">
            {busy === "undo" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Undo2 className="mr-2 h-4 w-4" /> Not paid after all</>}
          </Button>
        </div>
      ) : (
        <div className="mt-4 rounded-lg border border-ink-600 bg-ink-900/60 p-4">
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
            When does this chauffeur get paid?
          </p>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            {TIMINGS.map((t) => (
              <button key={t.key} type="button" data-testid={`driver-pay-timing-${t.key}`}
                onClick={() => setTiming(t.key)}
                className={`flex-1 rounded-lg border p-3 text-left transition-colors ${
                  timing === t.key ? "border-gold/60 bg-gold/10" : "border-ink-600 hover:border-gold/30"}`}>
                <span className="block text-sm text-slate-100">{t.label}</span>
                <span className="block text-xs text-slate-500">{t.hint}</span>
              </button>
            ))}
          </div>
          <p className="mt-3 text-xs text-slate-500">
            {timing ? "Saved with the pay amount above." : "Following your company default — set it in Settings → Payments."}
          </p>
          {payout.amount > 0 && (
            <Button data-testid="driver-pay-now" variant="outline" disabled={busy !== ""} onClick={payNow}
              className="mt-3 h-11 border-gold/40 bg-transparent text-gold hover:bg-gold/10">
              {busy === "pay" ? <Loader2 className="h-4 w-4 animate-spin" />
                : <>Mark {money(payout.amount)} paid now</>}
            </Button>
          )}
        </div>
      )}

      {payout.amount > 0 && (
        <p data-testid="driver-pay-current" className="mt-3 font-mono text-[11px] text-slate-500">
          Chauffeur currently sees {money(payout.amount)}{payout.label ? ` · ${payout.label}` : ""}
        </p>
      )}
      {saved && <p data-testid="driver-pay-saved" className="mt-2 text-sm text-emerald-400">Saved — send the manifest again if it has already gone out.</p>}
      {error && <p data-testid="driver-pay-error" className="mt-2 text-sm text-red-400">{error}</p>}
    </div>
  );
};
